/**
 * How It Works Page
 *
 * Full service overview: browse → checkout → pickup → enjoy → return.
 * Covers short-term rentals, monthly subscriptions, cancellation policy,
 * deposits, and extensions.
 */

import { Link } from 'react-router';
import {
  ArrowLeft,
  Calendar,
  Clock,
  XCircle,
  DollarSign,
  AlertTriangle,
  Check,
  RotateCcw,
} from 'lucide-react';

const steps = [
  {
    number: 1,
    title: 'Browse & Choose',
    description: 'Explore our catalog of premium baby equipment. Select your dates for a short-term rental, or choose a monthly subscription plan.',
  },
  {
    number: 2,
    title: 'Checkout & Pay',
    description: 'Pay securely via Stripe. A refundable security deposit is held to cover the equipment during your rental.',
  },
  {
    number: 3,
    title: 'Pick Up',
    description: 'Collect your equipment at your selected location. Bring your confirmation email and a valid ID. Our staff will walk you through everything.',
  },
  {
    number: 4,
    title: 'Enjoy',
    description: 'Use the equipment for as long as you need. Subscriptions renew monthly — extend or cancel at any time.',
  },
  {
    number: 5,
    title: 'Return',
    description: 'Bring the equipment back to the same location. After a quick inspection, your deposit is refunded within a few business days.',
  },
];

const subscriptionPlans = [
  {
    name: 'Flexible',
    commitment: 'None',
    highlight: 'Cancel anytime, no penalty',
  },
  {
    name: '3-Month',
    commitment: '3 months',
    highlight: 'Lower monthly rate',
  },
  {
    name: '6-Month',
    commitment: '6 months',
    highlight: 'Even lower rate',
  },
  {
    name: '12-Month',
    commitment: '12 months',
    highlight: 'Best monthly rate',
  },
];

export const RentalsInfoPage = () => {
  return (
    <>
      <div className="bg-white py-12 sm:py-16">
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          {/* Back Link */}
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center text-sm font-medium text-primary hover:text-primary"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Link>
          </div>

          {/* Hero */}
          <div className="text-center mb-16">
            <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
              How It Works
            </h1>
            <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto">
              Renting premium baby equipment is simple. Choose what you need, pick it up,
              and return it when you're done — with flexible rental and subscription options.
            </p>
          </div>

          {/* Step-by-Step Process */}
          <section className="mb-16">
            <div className="space-y-0">
              {steps.map((step, index) => {
                return (
                  <div key={step.number} className="relative flex gap-6 pb-10 last:pb-0">
                    {/* Connector line */}
                    {index < steps.length - 1 && (
                      <div className="absolute left-6 top-14 bottom-0 w-px bg-primary/20" />
                    )}
                    {/* Step number circle */}
                    <div className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary text-white font-bold text-lg shadow-md">
                      {step.number}
                    </div>
                    {/* Content */}
                      <div className="flex-1 pt-1">
                        <h3 className="text-lg font-semibold text-foreground mb-1">{step.title}</h3>
                        <p className="text-muted-foreground leading-relaxed">{step.description}</p>
                      </div>
                  </div>
                );
              })}
            </div>
          </section>

          <div className="space-y-12">
            {/* Rental Options */}
            <section className="bg-muted rounded-xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="h-6 w-6 text-primary" />
                <h2 className="text-2xl font-bold text-foreground">Rental Options</h2>
              </div>
              <div className="grid gap-6 md:grid-cols-2">
                <div className="bg-white rounded-lg p-6 border border-border">
                  <h3 className="font-semibold text-lg text-foreground mb-2">Short-Term Rentals</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                    Ideal for holidays, visits, or temporary needs. Choose your dates and pay upfront.
                  </p>
                  <ul className="space-y-2 text-sm text-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Tiered pricing — longer rentals get better daily rates</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>From 1 day to 40+ days</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                      <span>Refundable security deposit included</span>
                    </li>
                  </ul>
                </div>
                <div className="rounded-lg border border-primary/20 bg-white p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-foreground">Monthly Subscriptions</h3>
                    <span className="rounded-full bg-highlight-subtle px-2 py-0.5 text-xs font-medium text-highlight">Save more</span>
                  </div>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-3">
                    For longer-term needs. Pay monthly with automatic billing. You can cancel anytime,
                    but early cancellation of a commitment plan may incur a penalty.
                  </p>
                  <ul className="space-y-2 text-sm text-foreground">
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span>Flexible (no commitment) or 3/6/12 month plans</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span>Longer commitment = lower monthly rate</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span>Billed automatically via Stripe each month</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Monthly Rental Plans Detail */}
            <section className="bg-muted rounded-xl p-8">
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-2xl font-bold text-foreground">Monthly Rental Plans</h2>
              </div>
              <p className="text-muted-foreground mb-6">
                Choose the plan that suits your needs. All plans include a refundable security deposit
                and can be managed from your account.
              </p>

              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 mb-6">
                {subscriptionPlans.map((plan) => (
                  <div key={plan.name} className="rounded-lg border border-border bg-white p-4 text-center">
                    <p className="font-semibold text-foreground">{plan.name}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{plan.highlight}</p>
                  </div>
                ))}
              </div>

              <div className="bg-white rounded-lg p-5 border border-border space-y-3">
                <h4 className="font-semibold text-foreground">Cancellation & Early Termination</h4>
                <p className="text-sm text-foreground leading-relaxed">
                  <strong>You can cancel anytime.</strong> If you cancel before your commitment period ends,
                  an early cancellation fee may apply. The fee is based on the difference between
                  your discounted rate and the rate for the next shorter plan, multiplied by the months you used.
                </p>
                <div className="bg-warning-subtle border border-warning-border rounded-lg p-4">
                  <p className="text-sm text-warning-foreground">
                    <strong>Example:</strong> You committed to 6 months at &euro;23/month but cancel after 3 months.
                    The 3-month rate is &euro;26/month. Penalty = (&euro;26 &minus; &euro;23) &times; 3 months = &euro;9.
                  </p>
                </div>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• <strong>Flexible plan:</strong> No commitment, no penalty — cancel and return anytime.</li>
                  <li>• <strong>After your commitment ends:</strong> Continue month-to-month at the same rate, or return with no penalty.</li>
                </ul>
              </div>
            </section>

            {/* After Cancellation / Return Process */}
            <section className="bg-muted rounded-xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <RotateCcw className="h-6 w-6 text-primary" />
                <h2 className="text-2xl font-bold text-foreground">Return & Inspection</h2>
              </div>
              <div className="space-y-4 text-foreground">
                <p className="leading-relaxed">
                  When you cancel a subscription or your rental ends, here's what happens:
                </p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <div className="bg-white rounded-lg p-4 border border-border">
                    <h4 className="font-semibold text-foreground mb-1">Return</h4>
                    <p className="text-sm text-muted-foreground">
                      Bring the equipment back within <strong>7 days</strong> of cancellation.
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-border">
                    <h4 className="font-semibold text-foreground mb-1">Inspection</h4>
                    <p className="text-sm text-muted-foreground">
                      Our staff inspects the equipment within <strong>7 days</strong> of return.
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-border">
                    <h4 className="font-semibold text-foreground mb-1">Deposit Refund</h4>
                    <p className="text-sm text-muted-foreground">
                      Your deposit is refunded once the inspection is complete.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Security Deposits */}
            <section className="bg-muted rounded-xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <DollarSign className="h-6 w-6 text-primary" />
                <h2 className="text-2xl font-bold text-foreground">Security Deposits</h2>
              </div>
              <div className="space-y-4 text-foreground">
                <p className="leading-relaxed">
                  A refundable security deposit is required for all rentals and subscriptions. It protects
                  the equipment during your rental and is fully refunded when items are returned in good condition.
                </p>
                <div>
                  <h3 className="font-semibold text-lg text-foreground mb-2">Deductions may apply for:</h3>
                  <ul className="list-disc list-inside space-y-2 ml-4">
                    <li><strong>Damage</strong> beyond normal wear and tear</li>
                    <li><strong>Missing parts</strong> or accessories</li>
                    <li><strong>Loss</strong> of equipment</li>
                    <li><strong>Excessive cleaning</strong> required</li>
                    <li><strong>Unauthorised late returns</strong></li>
                  </ul>
                </div>
                <div className="p-4 bg-muted rounded-lg">
                  <h4 className="font-semibold text-foreground mb-2">Dispute Process</h4>
                  <p className="text-sm">
                    If you disagree with a deduction, contact us within 7 days. We'll review the inspection
                    photos and work with you to resolve the issue fairly.
                  </p>
                </div>
              </div>
            </section>

            {/* Cancellation Policy */}
            <section className="bg-destructive-subtle border-2 border-destructive-border rounded-xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <XCircle className="h-7 w-7 text-destructive" />
                <h2 className="text-2xl font-bold text-foreground">Cancellation Policy</h2>
              </div>

              <div className="bg-destructive-subtle border border-destructive-border rounded-lg p-4 mb-6">
                <div className="flex gap-3">
                  <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive font-medium">
                    <strong>Important:</strong> Cancellation fees apply based on when you cancel. Please read
                    this section carefully before making a reservation.
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                {/* Short-term rental cancellation */}
                <div>
                  <h3 className="font-bold text-lg text-foreground mb-4">Short-Term Rental Cancellations (Before Pickup)</h3>
                  <div className="space-y-3">
                    <div className="bg-white rounded-lg p-4 border-l-4 border-success">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-foreground">More than 7 days before pickup</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            <strong className="text-success">FREE CANCELLATION</strong> — 100% refund
                          </p>
                        </div>
                        <span className="text-2xl font-bold text-success">0%</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border-l-4 border-warning-border">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-foreground">3 to 7 days before pickup</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            <strong className="text-warning-foreground">25% cancellation fee</strong> — 75% refund
                          </p>
                        </div>
                        <span className="text-2xl font-bold text-warning">25%</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border-l-4 border-warning">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-foreground">1 to 2 days before pickup</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            <strong className="text-warning-foreground">50% cancellation fee</strong> — 50% refund
                          </p>
                        </div>
                        <span className="text-2xl font-bold text-warning">50%</span>
                      </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 border-l-4 border-destructive">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-semibold text-foreground">Same day as pickup</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            <strong className="text-destructive">100% cancellation fee</strong> — No refund
                          </p>
                        </div>
                        <span className="text-2xl font-bold text-destructive">100%</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Monthly rental cancellation */}
                <div className="border-t border-destructive-border pt-6">
                  <h3 className="font-bold text-lg text-foreground mb-4">Monthly Rental Cancellations</h3>
                  <p className="text-foreground mb-3">
                    You can cancel a monthly rental at any time. No need to complete your commitment period.
                    However, early cancellation of a commitment plan may result in a penalty:
                  </p>
                  <ul className="list-disc list-inside space-y-2 ml-4 text-foreground mb-4">
                    <li><strong>Flexible plan:</strong> No penalty at all — cancel and return anytime.</li>
                    <li><strong>Commitment plan (3/6/12 months):</strong> If cancelled early, you pay a fee based on the rate difference between your plan and the next shorter tier, multiplied by the months you used.</li>
                    <li><strong>After commitment ends:</strong> Cancel with no penalty — your rental continues month-to-month at the same rate.</li>
                  </ul>
                  <div className="bg-warning-subtle border border-warning-border rounded-lg p-4">
                    <p className="text-sm text-warning-foreground">
                      <strong>Monthly Rental Early Cancellation Example:</strong> You committed to 6 months at
                      &euro;23/month but cancel after 3 months. The 3-month rate is &euro;26/month.
                      Penalty = (&euro;26 &minus; &euro;23) &times; 3 = &euro;9.
                    </p>
                  </div>
                </div>

                {/* After pickup */}
                <div className="border-t border-destructive-border pt-6">
                  <h3 className="font-bold text-lg text-foreground mb-4">After Pickup (Early Return)</h3>
                  <div className="bg-white rounded-lg p-4 border border-border">
                    <ol className="list-decimal list-inside space-y-3 text-foreground">
                      <li>
                        <strong>Request cancellation through your account.</strong>
                      </li>
                      <li>
                        <strong>Return the equipment to your pickup location.</strong> Equipment must be in good condition.
                      </li>
                      <li>
                        <strong>Our staff inspects the equipment.</strong> This takes up to 7 days.
                      </li>
                      <li>
                        <strong>Deposit is refunded</strong> within 5–10 business days if no damage is found.
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </section>

            {/* Extensions */}
            <section className="bg-muted rounded-xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <Clock className="h-6 w-6 text-primary" />
                <h2 className="text-2xl font-bold text-foreground">Rental Extensions</h2>
              </div>
              <div className="space-y-4 text-foreground">
                <p className="leading-relaxed">
                  Need the equipment longer? You can request an extension through your account.
                </p>
                <ol className="list-decimal list-inside space-y-2 ml-4">
                  <li>Go to your reservation details and click &quot;Request Extension&quot;</li>
                  <li>Select your new return date</li>
                  <li>We'll check availability for the extended period</li>
                  <li>If available, you'll see the additional cost</li>
                  <li>Complete payment to confirm the extension</li>
                </ol>
                <div className="p-4 bg-info-subtle border border-info-border rounded-lg">
                  <p className="text-sm text-info">
                    <strong>Tip:</strong> Request extensions at least 24 hours before your return date.
                    Extensions are subject to availability, especially during peak seasons.
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* Footer CTA */}
          <div className="mt-12 bg-primary rounded-xl p-8 text-center text-white">
            <h3 className="text-xl font-semibold">Have questions?</h3>
            <p className="mt-2 text-primary-foreground/80">
              Our team is here to help with anything you need to know.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center rounded-lg bg-white px-6 py-3 text-sm font-semibold text-primary hover:bg-primary/5 transition-colors"
              >
                Contact Us
              </Link>
              <Link
                to="/faq"
                className="inline-flex items-center justify-center rounded-lg border-2 border-white px-6 py-3 text-sm font-semibold text-white hover:bg-primary transition-colors"
              >
                View FAQ
              </Link>
              <Link
                to="/catalogue"
                className="inline-flex items-center justify-center rounded-lg border-2 border-white px-6 py-3 text-sm font-semibold text-white hover:bg-primary transition-colors"
              >
                Browse Catalogue
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default RentalsInfoPage;
